/**
 * OAuth utility functions for Cognito authentication
 * Implements PKCE (Proof Key for Code Exchange) flow
 */

import { getCognitoEndpoints, clientId, getRedirectUri, scopes, cognitoDomain } from '../config/auth';

// Storage keys
const CODE_VERIFIER_KEY = 'oauth_code_verifier';
const STATE_KEY = 'oauth_state';
const REDIRECT_PATH_KEY = 'oauth_redirect_path';
const REDIRECT_URI_KEY = 'oauth_redirect_uri'; // Store the exact redirect URI used in auth request

/** Deduplicate concurrent exchanges of the same auth code (React Strict Mode remounts). */
const inFlightTokenExchanges = new Map<string, Promise<TokenResponse>>();

export interface TokenResponse {
  access_token: string;
  id_token: string;
  refresh_token?: string; // Optional - may not be included in refresh token response
  token_type?: string;
  expires_in?: number;
}

/**
 * Generate a random code verifier for PKCE
 * @returns {Promise<string>} Base64URL-encoded code verifier
 */
export async function generateCodeVerifier(): Promise<string> {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64URLEncode(array);
}

/**
 * Generate code challenge from verifier using SHA256
 * @param {string} verifier - The code verifier
 * @returns {Promise<string>} Base64URL-encoded code challenge
 */
export async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64URLEncode(new Uint8Array(digest));
}

/**
 * Base64URL encode (URL-safe base64)
 * @param {Uint8Array} array - Array to encode
 * @returns {string} Base64URL-encoded string
 */
function base64URLEncode(array: Uint8Array): string {
  // Convert Uint8Array to regular array to avoid spread operator issues
  const charCodes = Array.from(array);
  return btoa(String.fromCharCode(...charCodes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

/**
 * Generate a random state parameter for CSRF protection
 * @returns {string} Random state string
 */
export function generateState(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return base64URLEncode(array);
}

/**
 * Build Cognito authorization URL
 * @param {string} redirectPath - Optional path to redirect to after login
 * @returns {Promise<string>} Authorization URL
 */
export async function getAuthUrl(redirectPath: string | null = null): Promise<string> {
  try {
    const verifier = await generateCodeVerifier();
    const challenge = await generateCodeChallenge(verifier);
    const state = generateState();
    
    // Store verifier and state in sessionStorage for later use
    sessionStorage.setItem(CODE_VERIFIER_KEY, verifier);
    sessionStorage.setItem(STATE_KEY, state);
    if (redirectPath) {
      sessionStorage.setItem(REDIRECT_PATH_KEY, redirectPath);
    }
    
    const endpoints = getCognitoEndpoints();
    const redirectUri = getRedirectUri();
    
    // Store the exact redirect URI used - must match exactly in token exchange
    sessionStorage.setItem(REDIRECT_URI_KEY, redirectUri);
    
    // Log for debugging (remove in production if needed)
    console.log('OAuth configuration:', {
      redirectUri,
      clientId,
      cognitoDomain,
      scopes
    });
  
    const params = new URLSearchParams({
      client_id: clientId,
      response_type: 'code',
      scope: scopes,
      redirect_uri: redirectUri,
      code_challenge: challenge,
      code_challenge_method: 'S256',
      state: state
    });
    
    return `${endpoints.authorize}?${params.toString()}`;
  } catch (error) {
    console.error('Error generating auth URL:', error);
    throw new Error('Failed to generate authentication URL. Please check your configuration.');
  }
}

/**
 * Exchange authorization code for tokens
 * @param {string} code - Authorization code from callback
 * @param {string} state - State parameter from callback
 * @returns {Promise<TokenResponse>} Token response with access_token, id_token, refresh_token
 */
export async function exchangeCodeForTokens(code: string, state: string): Promise<TokenResponse> {
  const existing = inFlightTokenExchanges.get(code);
  if (existing) {
    return existing;
  }

  const exchangePromise = (async (): Promise<TokenResponse> => {
    // Verify state parameter
    const storedState = sessionStorage.getItem(STATE_KEY);
    if (!storedState || storedState !== state) {
      throw new Error('Invalid state parameter - possible CSRF attack');
    }

    // Get stored code verifier
    const verifier = sessionStorage.getItem(CODE_VERIFIER_KEY);
    if (!verifier) {
      throw new Error('Code verifier not found - please initiate login again');
    }

    // Get the exact redirect URI that was used in the authorization request
    // This MUST match exactly, or Cognito will return invalid_grant
    const storedRedirectUri = sessionStorage.getItem(REDIRECT_URI_KEY);
    const fallbackRedirectUri = getRedirectUri();
    const redirectUri = storedRedirectUri || fallbackRedirectUri;

    if (!redirectUri) {
      throw new Error('Redirect URI not found - please initiate login again');
    }

    const endpoints = getCognitoEndpoints();

    // Log for debugging - this is critical for diagnosing invalid_grant errors
    console.log('Token exchange parameters:', {
      redirectUri,
      storedRedirectUri: storedRedirectUri || 'not found in sessionStorage',
      fallbackRedirectUri,
      currentWindowOrigin: typeof window !== 'undefined' ? window.location.origin : 'N/A',
      currentWindowHref: typeof window !== 'undefined' ? window.location.href : 'N/A',
      clientId,
      hasVerifier: !!verifier,
      codeLength: code.length,
      state: state.substring(0, 10) + '...' // Log first 10 chars of state for debugging
    });

    // Warn if we're using fallback instead of stored redirect URI
    if (!storedRedirectUri) {
      console.warn('WARNING: Using fallback redirect URI instead of stored one. This may cause invalid_grant errors!');
    }

    const params = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: clientId,
      code: code,
      redirect_uri: redirectUri,
      code_verifier: verifier
    });

    const response = await fetch(endpoints.token, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: params.toString()
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(text)
    }

    const tokens = await response.json() as TokenResponse;

    // Clear stored verifier, state, and redirect URI after successful exchange
    sessionStorage.removeItem(CODE_VERIFIER_KEY);
    sessionStorage.removeItem(STATE_KEY);
    sessionStorage.removeItem(REDIRECT_URI_KEY);

    return tokens;
  })();

  inFlightTokenExchanges.set(code, exchangePromise);
  try {
    return await exchangePromise;
  } finally {
    // Keep successful exchanges cached briefly so a Strict Mode remount can reuse
    // the same result; drop failures so a fresh login can retry.
    exchangePromise.catch(() => {
      inFlightTokenExchanges.delete(code);
    });
  }
}

/**
 * Refresh access token using refresh token
 * @param {string} refreshToken - Refresh token
 * @returns {Promise<TokenResponse>} New token response
 */
export async function refreshAccessToken(refreshToken: string): Promise<TokenResponse> {
  const endpoints = getCognitoEndpoints();
  
  const params = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: clientId,
    refresh_token: refreshToken
  });
  
  const response = await fetch(endpoints.token, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    },
    body: params.toString()
  });
  
  if (!response.ok) {
    let error: { error?: string; error_description?: string } = { error: 'Unknown error' };
    try {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        error = await response.json() as { error?: string; error_description?: string };
      } else {
        const text = await response.text();
        throw new Error(`Token refresh failed: ${response.status} ${response.statusText}${text ? ` - ${text}` : ''}`);
      }
    } catch (parseError) {
      // If JSON parsing fails, use the parse error or create a generic one
      if (parseError instanceof Error && !parseError.message.includes('Token refresh failed')) {
        throw new Error(`Token refresh failed: ${response.status} ${response.statusText}`);
      }
      throw parseError;
    }
    
    const errorMessage = error?.error_description || error?.error || 'Token refresh failed';
    throw new Error(errorMessage);
  }
  
  return await response.json() as TokenResponse;
}

/**
 * Parse JWT token to extract payload
 * @param {string} token - JWT token
 * @returns {Record<string, any> | null} Decoded token payload or null if invalid
 */
export function parseJwt(token: string): Record<string, any> | null {
  try {
    if (!token || typeof token !== 'string') {
      return null;
    }
    
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('Invalid JWT format: token must have 3 parts');
      return null;
    }
    
    const base64Url = parts[1];
    if (!base64Url) {
      console.error('Invalid JWT format: missing payload');
      return null;
    }
    
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error parsing JWT:', error);
    return null;
  }
}

/**
 * Check if token is expired
 * @param {string} token - JWT token
 * @returns {boolean} True if token is expired or will expire within 5 minutes
 */
export function isTokenExpired(token: string): boolean {
  const decoded = parseJwt(token);
  if (!decoded || !decoded.exp) {
    return true;
  }
  
  // Consider token expired if it expires within 5 minutes
  const expirationTime = decoded.exp * 1000; // Convert to milliseconds
  const bufferTime = 5 * 60 * 1000; // 5 minutes
  return Date.now() >= (expirationTime - bufferTime);
}

/**
 * Get stored redirect path and clear it
 * @returns {string | null} Redirect path or null
 */
export function getAndClearRedirectPath(): string | null {
  const path = sessionStorage.getItem(REDIRECT_PATH_KEY);
  if (path) {
    sessionStorage.removeItem(REDIRECT_PATH_KEY);
  }
  return path;
}

/**
 * Build Cognito logout URL
 * @returns {string} Logout URL
 */
export function getLogoutUrl(): string {
  try {
    const endpoints = getCognitoEndpoints();
    const redirectUri = getRedirectUri().replace('/callback', '/logout');
    
    const params = new URLSearchParams({
      client_id: clientId,
      logout_uri: redirectUri
    });
    
    return `${endpoints.logout}?${params.toString()}`;
  } catch (error) {
    console.error('Error generating logout URL:', error);
    // Return a fallback URL that just clears local state
    return window.location.origin + '/logout';
  }
}

/** Cognito hosted UI forgot-password (change password) URL. */
export function getForgotPasswordUrl(): string {
  try {
    const redirectUri = getRedirectUri();
    const params = new URLSearchParams({
      client_id: clientId,
      response_type: 'code',
      scope: scopes,
      redirect_uri: redirectUri,
    });
    return `https://${cognitoDomain}/forgotPassword?${params.toString()}`;
  } catch (error) {
    console.error('Error generating forgot-password URL:', error);
    return `https://${cognitoDomain}/forgotPassword`;
  }
}
